import { useCallback } from 'react';

import { AxiosResponse } from 'axios';
import dayjs from 'dayjs';

import { QueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  TypeGetWorkApplicationsRecommendationsData,
  TypeGetWorkApplicationsRecommendationsResponse,
} from '~/api/workApplications';
import { registerMockAPI } from '~/mocks/mockApis';

import { axiosInstance } from '~/services/fetchServiceV2';
import { formatDate } from '~/utils';

export const RECOMMENDATIONS_QUERY_KEY = 'recommendations';

const recommendationsQueryFn = (workApplicationId: string) =>
  axiosInstance.get<TypeGetWorkApplicationsRecommendationsResponse>(
    `/work-applications/${workApplicationId}/recommendations`
  );

/** @description 추천 공고 목록 조회 (훅이 아닌 곳에서 사용) */
export const fetchRecommendations = async (
  queryClient: QueryClient,
  workApplicationId: string
): Promise<TypeGetWorkApplicationsRecommendationsData> => {
  const response = await queryClient.fetchQuery({
    queryKey: [RECOMMENDATIONS_QUERY_KEY, workApplicationId],
    queryFn: () => recommendationsQueryFn(workApplicationId),
  });
  return response.data.data;
};

/** @description 추천 공고 목록 조회 (컴포넌트에서 사용) */
export const useGetRecommendations = ({ workApplicationId }: { workApplicationId: string }) => {
  const handleResponse = useCallback(
    (response: AxiosResponse<TypeGetWorkApplicationsRecommendationsResponse>) => response.data.data,
    []
  );

  const { data } = useSuspenseQuery({
    queryKey: [RECOMMENDATIONS_QUERY_KEY, workApplicationId],
    queryFn: () => recommendationsQueryFn(workApplicationId),
    gcTime: 30 * 1000,
    retry: 1,
    select: (response) => handleResponse(response),
  });

  return { data };
};

/** mock 기준 날짜: 2026-01-19 (e2e fixture의 FIXED_DATE와 동일) */
const MOCK_BASE_DATE = dayjs('2026-01-19');

registerMockAPI<TypeGetWorkApplicationsRecommendationsResponse>({
  method: 'get',
  endpoint: '/work-applications/:workApplicationId/recommendations',
  label: '지원 정보 확인/추천 공고 목록 조회',
  scenarios: [
    {
      name: '추천 공고 있음 (1/2지망)',
      response: {
        code: '0000',
        message: '성공',
        data: {
          workLocationId: 1,
          workLocationName: '김포 물류센터',
          task: {
            code: 'OUTBOUND',
            text: '출고',
            description: '주문된 상품을 집품한 후, 주문자별로 분류⋅포장해요.',
          },
          commutingMethod: { code: 'SHUTTLE', text: '셔틀' },
          recommendations: [
            {
              workDate: formatDate({ value: MOCK_BASE_DATE.add(1, 'day') }),
              applications: [
                {
                  recruitNoticeId: '1',
                  priority: { code: 'FIRST', text: '1지망' },
                  workShift: '09:00 ~ 18:00',
                  dailyWage: 120000,
                  breakTimeMinutes: 60,
                  mealProvisionType: { code: 'LUNCH', text: '중식제공' },
                  workEnvironment: { code: 'NORMAL', text: '상온' },
                },
                {
                  recruitNoticeId: '2',
                  priority: { code: 'SECOND', text: '2지망' },
                  workShift: '13:00 ~ 22:00',
                  dailyWage: 110000,
                  breakTimeMinutes: 60,
                  mealProvisionType: { code: 'LUNCH', text: '중식제공' },
                  workEnvironment: { code: 'COLD', text: '냉장' },
                },
              ],
            },
            {
              workDate: formatDate({ value: MOCK_BASE_DATE.add(2, 'day') }),
              applications: [
                {
                  recruitNoticeId: '1',
                  priority: { code: 'FIRST', text: '1지망' },
                  workShift: '09:00 ~ 18:00',
                  dailyWage: 120000,
                  breakTimeMinutes: 60,
                  mealProvisionType: { code: 'LUNCH', text: '중식제공' },
                  workEnvironment: { code: 'NORMAL', text: '상온' },
                },
                {
                  recruitNoticeId: '2',
                  priority: { code: 'SECOND', text: '2지망' },
                  workShift: '16:00 ~ 00:50',
                  dailyWage: 115000,
                  breakTimeMinutes: 50,
                  mealProvisionType: { code: 'DINNER', text: '석식제공' },
                  workEnvironment: { code: 'COLD', text: '냉장' },
                },
              ],
            },
            {
              workDate: formatDate({ value: MOCK_BASE_DATE.add(3, 'day') }),
              applications: [
                {
                  recruitNoticeId: '1',
                  priority: { code: 'FIRST', text: '1지망' },
                  workShift: '09:00 ~ 18:00',
                  dailyWage: 120000,
                  breakTimeMinutes: 60,
                  mealProvisionType: { code: 'LUNCH', text: '중식제공' },
                  workEnvironment: { code: 'NORMAL', text: '상온' },
                },
              ],
            },
          ],
        },
      } satisfies TypeGetWorkApplicationsRecommendationsResponse,
    },
    {
      name: '추천 공고 있음 (1지망)',
      response: {
        code: '0000',
        message: '성공',
        data: {
          workLocationId: 1,
          workLocationName: '김포 물류센터',
          task: {
            code: 'OUTBOUND',
            text: '출고',
            description: '주문된 상품을 집품한 후, 주문자별로 분류⋅포장해요.',
          },
          commutingMethod: { code: 'SHUTTLE', text: '셔틀' },
          recommendations: [
            {
              workDate: formatDate({ value: MOCK_BASE_DATE.add(1, 'day') }),
              applications: [
                {
                  recruitNoticeId: '1',
                  priority: { code: 'FIRST', text: '1지망' },
                  workShift: '09:00 ~ 18:00',
                  dailyWage: 120000,
                  breakTimeMinutes: 60,
                  mealProvisionType: { code: 'LUNCH', text: '중식제공' },
                  workEnvironment: { code: 'NORMAL', text: '상온' },
                },
              ],
            },
          ],
        },
      } satisfies TypeGetWorkApplicationsRecommendationsResponse,
    },
    {
      name: '추천 공고 없음',
      response: {
        code: '0000',
        message: '성공',
        data: null,
      } as unknown as TypeGetWorkApplicationsRecommendationsResponse,
    },
    {
      name: '서버 에러',
      response: {
        code: '500',
        message: '서버 내부 오류가 발생했습니다.',
        data: null,
      } as unknown as TypeGetWorkApplicationsRecommendationsResponse,
      status: 500,
    },
  ],
});
